import React, { useState, useEffect, useCallback } from "react";
import API_BASE_URL from "./RestAPI";
import { showSuccessToast, showErrorToast } from "../constants/ToastService.js";
import { invalidateRequirementsCache } from "./Common Components/requirementsCache";

/**
 * Move-to-Live approval workflow for a single ticket.
 *
 * - Shows up only when the company feature flag is on (Configuration.enable_live_support_sprint).
 * - Users with the "Request Live Move" permission can raise a request once the
 *   ticket reaches a QA Pass / UAT Pass status.
 * - Users with the "Approve Live Move" permission can approve / reject a pending request.
 * - Once a decision is recorded it is shown to everyone (requester, approver, timestamps),
 *   so the rest of the team can see who acted on it.
 *
 * Props:
 *   ticketId         - the ticket's id (required)
 *   ticketStatusName - current status label, e.g. "QA Pass" (used to gate the request button)
 */
const STATUS_COLORS = {
  PENDING: "#d6a100",
  APPROVED: "#2e9e5b",
  REJECTED: "#c0392b",
};

const isQaOrUatPass = (statusName) => {
  if (!statusName) return false;
  const n = String(statusName).trim().toLowerCase();
  return n.includes("pass") && (n.includes("qa") || n.includes("uat"));
};

// Whether a ticket's status may raise a request. In general release-approval mode
// this honours the company's configured eligible statuses (empty = any allowed);
// in legacy live-support mode it falls back to the QA/UAT-pass rule.
const isStatusEligibleForRequest = (statusName) => {
  const releaseApproval = JSON.parse(
    localStorage.getItem("releaseApprovalEnabled") || "false"
  );
  if (releaseApproval) {
    let eligible = [];
    try {
      eligible = JSON.parse(localStorage.getItem("releaseEligibleStatuses") || "[]");
    } catch {
      eligible = [];
    }
    if (!Array.isArray(eligible) || eligible.length === 0) return true; // no restriction
    if (!statusName) return false;
    const norm = String(statusName).trim().toLowerCase();
    return eligible.some((s) => String(s).trim().toLowerCase() === norm);
  }
  return isQaOrUatPass(statusName);
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
});

const LiveMovePanel = ({ ticketId, ticketStatusName }) => {
  const userPermission = JSON.parse(
    localStorage.getItem("userPermissions") || "[]"
  );
  const canRequest = userPermission.includes("Request Live Move");
  const canApprove = userPermission.includes("Approve Live Move");

  console.log("[LiveMovePanel] userPermissions:", userPermission);
  console.log("[LiveMovePanel] canRequest (Request Live Move):", canRequest);
  console.log("[LiveMovePanel] canApprove (Approve Live Move):", canApprove);

  // Authoritative feature flag — seed from cache, then confirm with the backend so
  // the panel doesn't depend on the flag having been cached earlier. The panel is
  // available when EITHER the legacy Live Support flag OR the general Release
  // Approval flag is on for the company.
  const [featureEnabled, setFeatureEnabled] = useState(
    JSON.parse(localStorage.getItem("liveSupportEnabled") || "false") ||
      JSON.parse(localStorage.getItem("releaseApprovalEnabled") || "false")
  );

  // Backwards-compatible alias used by the rest of the component.
  const liveSupportEnabled = featureEnabled;
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    fetch(`${API_BASE_URL}core/feature-config/`, { headers: authHeaders() })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          const liveSupport = Boolean(data.data?.live_support_sprint);
          const releaseApproval = Boolean(data.data?.release_approval);
          setFeatureEnabled(liveSupport || releaseApproval);
          localStorage.setItem("liveSupportEnabled", JSON.stringify(liveSupport));
          localStorage.setItem("releaseApprovalEnabled", JSON.stringify(releaseApproval));
          localStorage.setItem(
            "releaseEligibleStatuses",
            JSON.stringify(data.data?.release_eligible_statuses || [])
          );
        }
      })
      .catch((err) => console.error("Failed to load feature config:", err));
  }, []);

  const fetchState = useCallback(() => {
    if (!ticketId) return;
    setLoading(true);
    fetch(`${API_BASE_URL}ticketing/live-move/?ticket_id=${ticketId}`, {
      method: "GET",
      headers: authHeaders(),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          setLatest(data.data?.latest || null);
          setHistory(data.data?.history || []);
        }
      })
      .catch((err) => console.error("Failed to load live move state:", err))
      .finally(() => setLoading(false));
  }, [ticketId]);

  useEffect(() => {
    if (liveSupportEnabled) fetchState();
  }, [fetchState, liveSupportEnabled]);

  if (!liveSupportEnabled) {
    console.log(
      "[LiveMovePanel] hidden — company feature 'liveSupportEnabled' is OFF " +
        "(localStorage.liveSupportEnabled / Configuration.enable_live_support_sprint). " +
        "The whole Move-to-Live panel is not rendered."
    );
    return null;
  }

  const activeRequest =
    latest && (latest.request_status === "PENDING" || latest.request_status === "APPROVED")
      ? latest
      : null;

  // Diagnose why the "Send Request to Move to Live" button shows or not.
  const statusEligible = isStatusEligibleForRequest(ticketStatusName);
  const requestGate = {
    canRequest,
    hasNoActiveRequest: !activeRequest,
    statusEligible,
    ticketStatusName,
    activeRequestStatus: activeRequest?.request_status || null,
  };
  if (canRequest && !activeRequest && statusEligible) {
    console.log("[LiveMovePanel] request button VISIBLE — all conditions met", requestGate);
  } else {
    const reasons = [];
    if (!canRequest) reasons.push("missing 'Request Live Move' permission");
    if (activeRequest) reasons.push(`a ${activeRequest.request_status} request already exists`);
    if (!statusEligible)
      reasons.push(`ticket status '${ticketStatusName || "unknown"}' is not eligible to request`);
    console.log("[LiveMovePanel] request button HIDDEN because:", reasons, requestGate);
  }

  const raiseRequest = () => {
    setSubmitting(true);
    fetch(`${API_BASE_URL}ticketing/live-move/`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ ticket_id: ticketId, request_note: note }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          showSuccessToast("Live move request sent");
          setNote("");
          fetchState();
        } else {
          showErrorToast(data.message || "Could not send request");
        }
      })
      .catch(() => showErrorToast("Could not send request"))
      .finally(() => setSubmitting(false));
  };

  const decide = (decision) => {
    if (!latest?.id) return;
    setSubmitting(true);
    fetch(`${API_BASE_URL}ticketing/live-move/`, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify({
        id: latest.id,
        decision,
        decision_note: note,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          showSuccessToast(`Request ${decision.toLowerCase()}`);
          setNote("");
          invalidateRequirementsCache(); // an approval can move its requirement to Production
          fetchState();
        } else {
          showErrorToast(data.message || "Could not update request");
        }
      })
      .catch(() => showErrorToast("Could not update request"))
      .finally(() => setSubmitting(false));
  };

  const showRequestButton =
    canRequest && !activeRequest && statusEligible;

  return (
    <div
      style={{
        marginTop: "16px",
        padding: "14px 16px",
        border: "1px solid #444",
        borderRadius: "8px",
        background: "#1e1e1e",
      }}
    >
      <h3 style={{ margin: "0 0 10px", color: "#fff" }}>Move to Live</h3>

      {loading && <p style={{ color: "#aaa" }}>Loading…</p>}

      {/* Current request status — visible to everyone */}
      {latest && (
        <div style={{ marginBottom: "12px" }}>
          <span
            style={{
              display: "inline-block",
              padding: "2px 10px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: 600,
              color: "#fff",
              background: STATUS_COLORS[latest.request_status] || "#555",
            }}
          >
            {latest.request_status}
          </span>
          <div style={{ color: "#ccc", fontSize: "13px", marginTop: "6px" }}>
            Requested by <b>{latest.requested_by || "—"}</b>
            {latest.requested_on
              ? ` on ${new Date(latest.requested_on).toLocaleString()}`
              : ""}
            {latest.ticket_status_at_request
              ? ` (status: ${latest.ticket_status_at_request})`
              : ""}
          </div>
          {latest.request_note && (
            <div style={{ color: "#aaa", fontSize: "13px" }}>
              Note: {latest.request_note}
            </div>
          )}
          {latest.approved_by && (
            <div style={{ color: "#ccc", fontSize: "13px", marginTop: "4px" }}>
              {latest.request_status === "APPROVED" ? "Approved" : "Rejected"} by{" "}
              <b>{latest.approved_by}</b>
              {latest.approved_on
                ? ` on ${new Date(latest.approved_on).toLocaleString()}`
                : ""}
              {latest.decision_note ? ` — ${latest.decision_note}` : ""}
            </div>
          )}
        </div>
      )}

      {!latest && !loading && (
        <p style={{ color: "#888", fontSize: "13px" }}>
          No live move request yet for this ticket.
        </p>
      )}

      {/* Optional note input (used by both request and decision) */}
      {(showRequestButton ||
        (canApprove && latest?.request_status === "PENDING")) && (
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note…"
          rows={2}
          style={{
            width: "100%",
            background: "#121212",
            color: "#fff",
            border: "1px solid #555",
            borderRadius: "6px",
            padding: "8px",
            marginBottom: "10px",
            resize: "vertical",
          }}
        />
      )}

      <div style={{ display: "flex", gap: "10px" }}>
        {showRequestButton && (
          <button
            onClick={raiseRequest}
            disabled={submitting}
            style={{
              background: "#2e6fd6",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "8px 14px",
              cursor: submitting ? "not-allowed" : "pointer",
            }}
          >
            Send Request to Move to Live
          </button>
        )}

        {canApprove && latest?.request_status === "PENDING" && (
          <>
            <button
              onClick={() => decide("APPROVED")}
              disabled={submitting}
              style={{
                background: "#2e9e5b",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                padding: "8px 14px",
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              Approve
            </button>
            <button
              onClick={() => decide("REJECTED")}
              disabled={submitting}
              style={{
                background: "#c0392b",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                padding: "8px 14px",
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              Reject
            </button>
          </>
        )}
      </div>

      {/* Explain why the request button is hidden, so it isn't a mystery. */}
      {canRequest && !activeRequest && !statusEligible && (
        <p style={{ color: "#d6a100", fontSize: "12px", marginTop: "8px" }}>
          This ticket's current status (<b>{ticketStatusName || "unknown"}</b>) is
          not eligible to request a move to live.
        </p>
      )}
      {!canRequest && !canApprove && (
        <p style={{ color: "#888", fontSize: "12px", marginTop: "8px" }}>
          You don't have permission to request or approve live moves.
        </p>
      )}

      {/* History (so the whole team can see prior decisions) */}
      {history.length > 1 && (
        <details style={{ marginTop: "12px" }}>
          <summary style={{ color: "#aaa", cursor: "pointer", fontSize: "13px" }}>
            History ({history.length})
          </summary>
          <ul style={{ margin: "8px 0 0", paddingLeft: "18px", color: "#bbb", fontSize: "12px" }}>
            {history.map((h) => (
              <li key={h.id} style={{ marginBottom: "4px" }}>
                <b style={{ color: STATUS_COLORS[h.request_status] || "#bbb" }}>
                  {h.request_status}
                </b>{" "}
                — req by {h.requested_by || "—"}
                {h.approved_by ? `, acted by ${h.approved_by}` : ""}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default LiveMovePanel;
